import type React from "react";
import { useState, useCallback, useRef, useEffect } from "react";

interface UseResizableOptions {
  minWidth: number;
  minHeight: number;
  maxWidth?: number;
  maxHeight?: number;
  defaultWidth?: number;
  defaultHeight?: number;
}

type Edge = "right" | "bottom" | "corner";

export function useResizable({
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  defaultWidth,
  defaultHeight,
}: UseResizableOptions) {
  const [size, setSize] = useState<{ width: number | null; height: number | null }>({
    width: defaultWidth ?? null,
    height: defaultHeight ?? null,
  });

  const dragging = useRef<{
    edge: Edge;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const clamp = useCallback(
    (w: number, h: number) => {
      const cw = Math.max(minWidth, maxWidth ? Math.min(w, maxWidth) : w);
      const ch = Math.max(minHeight, maxHeight ? Math.min(h, maxHeight) : h);
      return { width: cw, height: ch };
    },
    [minWidth, minHeight, maxWidth, maxHeight]
  );

  const onPointerDown = useCallback(
    (edge: Edge) => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      dragging.current = {
        edge,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: size.width ?? rect.width,
        startHeight: size.height ?? rect.height,
      };
      document.body.style.cursor =
        edge === "right" ? "ew-resize" : edge === "bottom" ? "ns-resize" : "nwse-resize";
      document.body.style.userSelect = "none";
    },
    [size]
  );

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const d = dragging.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      let w = d.startWidth;
      let h = d.startHeight;
      if (d.edge === "right" || d.edge === "corner") w = d.startWidth + dx;
      if (d.edge === "bottom" || d.edge === "corner") h = d.startHeight + dy;
      const clamped = clamp(w, h);
      setSize(clamped);
    };

    const onPointerUp = () => {
      if (dragging.current) {
        dragging.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [clamp]);

  /**
   * Le style de la boîte, borné à l'écran.
   *
   * ── Le défaut corrigé ──────────────────────────────────────────────────────
   * Chaque modale posait sa largeur en dur — `width: size.width ?? 680`. Sur un
   * téléphone de 375 px, le wizard de cas débordait de 305 px et devenait
   * inutilisable : c'est pourtant la brique la plus demandée du tunnel de
   * campagne. Le `clamp` ci-dessus borne bien le REDIMENSIONNEMENT à la
   * fenêtre, mais personne ne redimensionne au doigt — c'est la taille
   * INITIALE qui débordait.
   *
   * On rend donc le style au lieu de le laisser composer par chaque appelant :
   * une seule source de vérité, et toute nouvelle modale en hérite.
   *
   * `min()` en CSS plutôt qu'un calcul JS : la valeur suit la rotation de
   * l'écran et l'ouverture du clavier virtuel sans qu'on ait à réagir.
   */
  const boxStyle = useCallback(
    (defaultWidth: number, defaultHeight: number): React.CSSProperties => ({
      width: `min(${size.width ?? defaultWidth}px, calc(100vw - 1rem))`,
      height: `min(${size.height ?? defaultHeight}px, calc(100dvh - 1rem))`,
    }),
    [size],
  );

  return { size, containerRef, onPointerDown, boxStyle };
}
